import { type RankingAllData } from '@prisma/client'
import { animated, useSpring } from '@react-spring/web'
import { scaleLinear, scaleBand } from '@visx/scale'
import { Bar } from '@visx/shape'

const width = 300
const height = 150
const xMax = width
const yMax = height

type Props = {
	data: Array<{ count: number; bucket: string; group: string }>
	runner?: RankingAllData
	color?: string
}
export default function Histogram(props: Props) {
	const { scale } = useSpring({
		from: { scale: 0 },
		to: { scale: 1 },
	})

	const xScale = scaleBand({
		range: [0, xMax],
		round: true,
		domain: props.data.map(entry => entry.bucket),
		padding: 0.25,
	})

	const yScale = scaleLinear({
		range: [yMax, 0],
		round: true,
		domain: [0, Math.max(...props.data.map(entry => entry.count))],
	})

	const compose = (scale: any, accessor: any) => (data: any) => scale(accessor(data))
	const xPoint = compose(xScale, (entry: any) => entry.bucket)
	const yPoint = compose(yScale, (entry: any) => entry.count)

	const groups = Array.from(new Set(props.data.map(datum => datum.group)))
	const AnimatedBar = animated(Bar)

	return (
		<div className="flex w-full justify-center">
			<svg className="rounded-md">
				<g className="bars" style={{ transformOrigin: 'bottom center' }}>
					{groups.map(group => {
						const color =
							props.color ?? (group === 'm' ? '#0000FF' : group === 'f' ? '#FF1493' : '#EAB308')
						return props.data
							.filter(d => d.group === group)
							.map((d, i) => {
								const barHeight = yMax - yPoint(d)
								return (
									<AnimatedBar
										key={`${d.group}-${d.bucket}`} // Maybe can't animate since buckets will not be consistent across renders?
										x={xPoint(d)}
										y={scale.to(s => yMax - s * barHeight)}
										height={scale.to(s => s * barHeight)}
										width={xScale.bandwidth()}
										strokeWidth={'1px'}
										opacity={0.75}
										rx={1}
										radius={'5px'}
										fill={color}
									/>
								)
							})
					})}
				</g>
			</svg>
		</div>
	)
}
